import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Typography, Card, Tag, Button, Result } from "antd";
import {
  ThunderboltOutlined,
  DollarOutlined,
  ArrowRightOutlined,
  ArrowLeftOutlined,
  CloudServerOutlined,
  CheckCircleOutlined,
  InfoCircleOutlined,
  RightOutlined,
} from "@ant-design/icons";
import PageLayout from "../pageLayout/PageLayout.react";
import "./engineGuide.css";

import imgEngineStatus from "../portalGuide/screenshots/engine_status.png";
import imgSettlements  from "../portalGuide/screenshots/settlements.png";

const { Title, Text, Paragraph } = Typography;

// ---------------------------------------------------------------------------
// DOCUMENTATION DATA
// ---------------------------------------------------------------------------

const ENGINES = [
  // ─────────────────────────────────────────────────────────────────────────
  // REALTIME ENGINE
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "realtime",
    icon: <ThunderboltOutlined />,
    color: "#1c3557",
    accentColor: "#e8a020",
    title: "Realtime Engine",
    tagLabel: "Live Processing",
    tagColor: "blue",
    summary:
      "Polls live telemetry from inverter/battery controllers via BullMQ, bins raw readings into NEM 5-minute intervals (288/day), aggregates to group-level portfolio snapshots, and persists results to Cassandra and PostgreSQL.",
    sections: [
      {
        type: "intro",
        content:
          "The Realtime Engine is a BullMQ-based pipeline (4 job queues) that continuously polls registered battery/solar system controllers for raw telemetry, resamples the variable-frequency data into fixed 5-minute NEM intervals, computes group-level headroom/footroom for VPP bidding, and writes canonical snapshot records to PostgreSQL. It is the single source of truth for all live dashboard charts and the prerequisite data source for the Settlement Engine.",
      },

      // ── SCHEDULER ──────────────────────────────────────────────────────────
      {
        type: "stages",
        title: "BullMQ Pipeline (4 Queues in Order)",
        stages: ["re-system-ingest", "re-group-realtime", "re-system-aggregation", "re-group-aggregation"],
      },
      {
        type: "section",
        title: "Scheduler Architecture",
        items: [
          {
            heading: "Queue Technology",
            body: "BullMQ with a Redis backend. All four queues are initialised from backend/src/routes/queues.js. Redis connection resolves from env vars REDIS_HOST / REDIS_PORT / REDIS_PASSWORD (defaults: localhost:6379).",
          },
          {
            heading: "Poll Interval",
            body: "Default poll interval is 5 minutes (300,000 ms), defined in engineController.js as pollIntervalMs: 5 * 60 * 1000. The scheduler state is persisted in Redis under key re:scheduler:running. Start/pause endpoints are POST /api/engine/realtime/scheduler/start and /pause.",
          },
          {
            heading: "Dispatch Trigger Channel",
            body: "A Redis pub/sub channel de:scheduler:trigger is used to signal the Dispatch Engine when realtime aggregation completes, keeping the two engines loosely coupled.",
          },
          {
            heading: "Job Retry Policy",
            body: "All BullMQ jobs use 3 attempts with exponential backoff starting at 5 s. Completed jobs are removed after 1 hour (max 500 kept). Failed jobs are retained for 7 days (max 1,000). Aggregation jobs use a 1-day retention with max 50 completed / 200 failed.",
          },
          {
            heading: "Metrics Collection",
            body: "Redis metrics are sampled every 60 seconds. 4,320 data points are retained (3 days at 1-min resolution). Tracked: opsPerSec, usedMemoryBytes, connectedClients, totalKeys, hitRate, evictedKeys, fragmentationRatio.",
          },
        ],
      },

      // ── POLL CYCLE ─────────────────────────────────────────────────────────
      {
        type: "section",
        title: "Poll Cycle: Step-by-Step Business Logic",
        items: [
          {
            heading: "Step 1 — re-system-ingest (Poll)",
            body: "Triggered via POST /api/engine/realtime/trigger-poll or automatically by the scheduler. A BullMQ job (jobType: 'poll', mode: 'POLL', batchSize: 5000) is enqueued with jobId manual-poll-{timestamp}. The worker fetches all active systems from the np_system PostgreSQL table, queries each system's controller vendor API (Alpha ESS, Sungrow, Growatt, Goodwe, Huawei, Fronius, SolarEdge, Powow Meter), and writes the raw api_response JSON to Cassandra table shinehub_system_data. Partition key: ((sn, date), vendor, api_name, created).",
          },
          {
            heading: "Step 2 — re-group-realtime",
            body: "Chained after ingest. Reads the freshly written Cassandra rows for the current interval window, parses vendor-specific payloads (sign convention normalisation — see Vendor Parsing below), and produces per-system power readings for the current 5-minute bucket. Results are written to Cassandra table powow_np_system_latest (PK: sn), updating fields: pv_kw, load_kw, pmeter_kw, pbat_kw, soc, headroom_kw, footroom_kw, online, data_ts, work_mode, dispatch_active, dispatch_command, dispatch_target_kw, dispatch_actual_kw, dispatch_compliance.",
          },
          {
            heading: "Step 3 — re-system-aggregation (5-min Bucketing)",
            body: "Chained after group-realtime. Function cleanAndResample() in realtimeDataService.js groups all Cassandra rows for the day by SN, bins them into BUCKET_MS = 5 * 60 * 1000 ms buckets, and averages power within each bucket. Output is a fixed 288-element array per SN — one element per 5-minute interval from 00:00 to 23:55 AEST. Day-start calculation: dayStartAest = UTC midnight − 10 h offset. Missing intervals are filled with null. Backfill jobs use force: true and jobId pattern backfill-{day}-{baseStamp}-{index}.",
          },
          {
            heading: "Step 4 — re-group-aggregation (Portfolio Snapshot)",
            body: "Final step. Function aggregateForGroup(cleanedBySn, systemSpecs) in realtimeDataService.js iterates all 288 time buckets. At each bucket it sums pv, load, pbat, pmeter across all systems and averages soc. It also calculates headroom = min(inverter_kw, battery_kwh × (soc/100) × C_rate) and footroom = min(inverter_kw, battery_kwh × ((100-soc)/100) × C_rate). Results are upserted into PostgreSQL tables np_view_group_daily_snapshot and np_control_group_daily_snapshot using ON CONFLICT (group_id, date) DO UPDATE.",
          },
        ],
      },

      // ── VENDOR PARSING ─────────────────────────────────────────────────────
      {
        type: "section",
        title: "Vendor-Specific Data Parsing (Sign Conventions)",
        items: [
          {
            heading: "Alpha ESS (default convention)",
            body: "pbat > 0 = discharge, pmeter > 0 = import from grid. PV is split across four strings: ppv = Ppv1 + Ppv2 + Ppv3 + Ppv4. Inverter power: pinv = ppv + pbat. House load: load = pinv + pmeter + pmeterDc.",
          },
          {
            heading: "Powow Meter (inverted battery convention)",
            body: "pbat < 0 = discharge (opposite to Alpha ESS), pmeter > 0 = import. PV: ppv = PpvAll ?? Ppv. Inverter power: pinv = ppv − pbat. House load: load = pinv + pmeter. The parser in realtimeDataService.js normalises these to a canonical sign convention before bucketing.",
          },
          {
            heading: "Sungrow, Growatt, Goodwe, Huawei, Fronius, SolarEdge",
            body: "These vendors follow close variations of the Alpha ESS convention with minor field name differences. Parsing is vendor-aware via a switch/case in parseApiResponse(). All vendors emit to the same canonical { pv, load, pbat, pmeter, soc } structure after parsing.",
          },
        ],
      },

      // ── DB OPS ─────────────────────────────────────────────────────────────
      {
        type: "table",
        title: "Database Operations",
        headers: ["Operation", "Database", "Table", "Trigger"],
        rows: [
          ["Write raw telemetry", "Cassandra", "shinehub_system_data", "re-system-ingest job"],
          ["Upsert live state", "Cassandra", "powow_np_system_latest", "re-group-realtime job"],
          ["Read today's rows for bucketing", "Cassandra", "shinehub_system_data", "re-system-aggregation job"],
          ["Upsert view-group snapshot (288 arrays)", "PostgreSQL", "np_view_group_daily_snapshot", "re-group-aggregation job"],
          ["Upsert control-group snapshot (288 arrays)", "PostgreSQL", "np_control_group_daily_snapshot", "re-group-aggregation job"],
          ["Read active systems list", "PostgreSQL", "np_system", "re-system-ingest job (poll start)"],
        ],
      },

      // ── REDIS KEYS ─────────────────────────────────────────────────────────
      {
        type: "table",
        title: "Redis Key Patterns",
        headers: ["Key / Pattern", "Type", "Purpose"],
        rows: [
          ["re:scheduler:running", "String", "Scheduler on/off state — toggled by start/pause endpoints"],
          ["bull:re-system-ingest:*", "BullMQ keys", "Job queue for controller telemetry polling"],
          ["bull:re-group-realtime:*", "BullMQ keys", "Job queue for live per-group calculations"],
          ["bull:re-system-aggregation:*", "BullMQ keys", "Job queue for 5-min interval bucketing per system"],
          ["bull:re-group-aggregation:*", "BullMQ keys", "Job queue for final group snapshot publication"],
          ["de:scheduler:trigger", "Pub/Sub channel", "Signals Dispatch Engine after aggregation completes"],
        ],
      },

      // ── SNAPSHOT FIELDS ────────────────────────────────────────────────────
      {
        type: "table",
        title: "PostgreSQL Snapshot Schema (np_view_group_daily_snapshot / np_control_group_daily_snapshot)",
        headers: ["Column", "Type", "Description"],
        rows: [
          ["date, [view|control]_group_id", "PK", "Composite primary key; upserted on conflict"],
          ["system_count, online_count, activated_count", "INT", "Fleet counts at time of snapshot"],
          ["total_inverter_kw, total_battery_kwh", "DECIMAL", "Group nameplate capacity"],
          ["today_credit, today_debit", "JSON[288]", "Per-interval revenue arrays (credit = export earnings, debit = import cost)"],
          ["today_ppv, today_pbat, today_pmeter, today_soc", "JSON[288]", "Per-interval power and SoC arrays"],
          ["today_headroom, today_footroom", "JSON[288]", "Per-interval available discharge / charge headroom"],
          ["today_dispatch_cmd, today_dispatch_actual", "JSON[288]", "Per-interval commanded vs actual dispatch power"],
          ["total_credit, total_debit, total_net", "DECIMAL", "Daily financial totals"],
          ["total_epv, total_eload, total_ebuy, total_esell", "DECIMAL", "Daily energy totals (kWh)"],
          ["source_run_id", "VARCHAR", "Links snapshot to the settlement run that last wrote it"],
        ],
      },

      // ── INTRADAY REDIS CACHE ───────────────────────────────────────────────
      {
        type: "section",
        title: "Intraday Redis Aggregation Cache",
        items: [
          {
            heading: "Purpose & Key Pattern",
            body: "Rather than reading Cassandra on every poll cycle, each in-progress day is accumulated in a Redis hash. Key pattern: re:intraday:sys:{sn}:{YYYY-MM-DD}. TTL = 26 hours (93,600 s), ensuring cleanup even if the midnight rollover job is delayed. The cache is the fast path; Step 3 (re-system-aggregation) reads Cassandra directly to produce the authoritative daily record.",
          },
          {
            heading: "Accumulator Structure",
            body: "Each Redis value is a JSON object: { pmeter_288, pinv_288, pbat_288, pv_288, ppvac_288, ppvdc_288, load_288, headroom_288, footroom_288, online_288 — all 288-slot null arrays updated in place; socSum, socCount, socMin, socMax — running stats; errorCount, warningCount, errorCodes — quality tracking; socHealthyCount — intervals where 20 ≤ soc ≤ 80%; daylightIntervalsWithData, daylightIntervalsWithPv — 6 am–7 pm intervals with data/pv > 0.1 kW; inverterKw, batteryKwh, timezone — system specs; maxCreatedUtc: null — always null for intraday; non-null signals Step 3 completion }.",
          },
          {
            heading: "Per-Poll Update Flow",
            body: "On each Step 1 poll: (1) Read accumulator from Redis. (2) Compute slot index = periodId − 1 (periodId is the NEM 5-min period 1–288 for the current timestamp). (3) Write polled values into slot: pmeter_288[idx], pbat_288[idx], pv_288[idx], etc. (4) Build Cassandra write params from the full 288-point accumulator. (5) INSERT INTO powow_np_system_aggregation_day. (6) Re-save accumulator to Redis with 26 h TTL. Both Cassandra and Redis are updated atomically in the same poll cycle.",
          },
          {
            heading: "Step 3 vs Intraday Writes",
            body: "Intraday writes always set source_max_created_utc = null. Step 3 (full daily aggregation, runs at 4:30 AM AEST) reads raw shinehub_system_data, re-runs cleanAndResample, and writes a final row with source_max_created_utc = non-null timestamp. Downstream services use source_max_created_utc IS NOT NULL to identify authoritative records and skip intraday rows.",
          },
        ],
      },

      // ── ENERGY MATH ────────────────────────────────────────────────────────
      {
        type: "section",
        title: "Energy Integration & Sign Conventions",
        items: [
          {
            heading: "kW → kWh Conversion (÷ 12 Rule)",
            body: "Each 5-minute interval represents 1/12 of an hour. To convert a power reading (kW) sampled in a 5-min bucket to energy (kWh): energy_kwh = power_kw / 12. This is applied when computing total_energy_import_kwh, total_pv_energy_kwh, total_bat_charge_kwh, etc. from their respective 288-slot arrays.",
          },
          {
            heading: "Grid Power Sign Convention (pmeter)",
            body: "pmeter > 0 = import from grid (household consuming from grid). pmeter < 0 = export to grid (battery/solar exporting). In the accumulator: if pmeter > 0 → totalImport += pmeter / 12; else totalExport += |pmeter| / 12.",
          },
          {
            heading: "Battery Power Sign Convention (pbat)",
            body: "pbat > 0 = discharging (battery supplying power). pbat < 0 = charging (battery absorbing power). In the accumulator: if pbat > 0 → totalBatDischarge += pbat / 12; else totalBatCharge += |pbat| / 12. Note: Powow Meter vendor uses the OPPOSITE convention (pbat < 0 = discharge) and is normalised by parseApiResponse() before any accumulation.",
          },
          {
            heading: "Headroom & Footroom Formulas",
            body: "Per system at each 5-min interval — headroom_kw = min(inverter_capacity_kw, battery_capacity_kwh × (current_soc_pct / 100)). footroom_kw = min(inverter_capacity_kw, battery_capacity_kwh × ((100 − current_soc_pct) / 100)). Group totals are the sum of all online system values at that interval. Headroom = how much power the group can discharge right now. Footroom = how much power the group can absorb (charge) right now.",
          },
        ],
      },

      // ── PERFORMANCE SCORING ────────────────────────────────────────────────
      {
        type: "table",
        title: "System Performance Score (0–100, written to powow_np_system_aggregation_day)",
        headers: ["Component", "Weight", "How Calculated"],
        rows: [
          ["data_availability", "25%", "% of 288 intervals that have a non-null reading for the day"],
          ["uptime",            "20%", "Same as data_availability — duplicate weight for stability emphasis"],
          ["soc_health",        "20%", "% of intervals where battery SoC is between 20% and 80% (healthy operating range)"],
          ["pv_efficiency",     "15%", "% of daylight intervals (6 am–7 pm AEST) where pv_kw > 0.1 kW"],
          ["dispatch_compliance","20%","dispatchCompliantCount / dispatchCount × 100. Default = 100 if no dispatch events occurred"],
        ],
      },
      {
        type: "callout",
        variant: "info",
        content:
          "performance_score = Σ (component_score × weight / 100). The breakdown JSON (performance_score_breakdown) and weights JSON (performance_score_weights) are stored alongside the score in Cassandra so the calculation can always be audited and the weights changed retroactively for historical re-runs.",
      },

      // ── BACKFILL ───────────────────────────────────────────────────────────
      {
        type: "section",
        title: "Backfill & Historical Aggregation",
        items: [
          {
            heading: "Trigger",
            body: "POST /api/engine/realtime/trigger-backfill with body { sns: string[], startDate: 'YYYY-MM-DD', endDate: 'YYYY-MM-DD' }. Time zone: Australia/Brisbane (AEST). Max range: 365 days.",
          },
          {
            heading: "Job Generation",
            body: "For each day in the date range the engine enqueues one re-system-aggregation job with force: true and jobId pattern backfill-{day}-{baseStamp}-{index}. Force mode discards any cached partial results and recomputes from raw Cassandra rows.",
          },
          {
            heading: "Retry",
            body: "3 attempts, exponential backoff starting at 30 s (longer than normal poll jobs due to larger data volume per job). Failed backfill jobs surface in the dead-letter API at GET /api/engine/dead-letters.",
          },
        ],
      },

      // ── API ENDPOINTS ──────────────────────────────────────────────────────
      {
        type: "table",
        title: "API Endpoints",
        headers: ["Method", "Path", "Description"],
        rows: [
          ["GET",  "/api/engine/realtime/status",              "Engine heartbeat, queue depths, error summary"],
          ["POST", "/api/engine/realtime/trigger-poll",        "Enqueue one manual poll cycle (all systems)"],
          ["POST", "/api/engine/realtime/trigger-backfill",    "Enqueue historical re-aggregation for date range"],
          ["POST", "/api/engine/realtime/trigger-aggregation", "Enqueue ad-hoc aggregation for specific SNs / date"],
          ["GET",  "/api/engine/realtime/scheduler/status",   "Read re:scheduler:running Redis key"],
          ["POST", "/api/engine/realtime/scheduler/start",    "Set scheduler running, begin 5-min poll loop"],
          ["POST", "/api/engine/realtime/scheduler/pause",    "Pause scheduler (does not drain in-flight jobs)"],
          ["GET",  "/api/engine/dead-letters",                "List failed jobs across all engine queues"],
        ],
      },

      {
        type: "callout",
        variant: "warning",
        content:
          "Restarting the Realtime Engine mid-poll will leave partial Cassandra writes. The next poll will overwrite them but dashboard charts may briefly show stale data for the current 5-minute interval. Never restart during active VPP dispatch — use the pause endpoint first.",
      },
      {
        type: "screenshot",
        src: imgEngineStatus,
        caption: "Engine Status page — monitor scheduler state, queue depths, and Realtime Engine heartbeat",
      },
    ],
  },

  // ─────────────────────────────────────────────────────────────────────────
  // SETTLEMENT ENGINE
  // ─────────────────────────────────────────────────────────────────────────
  {
    id: "settlement",
    icon: <DollarOutlined />,
    color: "#52c41a",
    accentColor: "#1c3557",
    title: "Settlement Engine",
    tagLabel: "Billing & Finance",
    tagColor: "green",
    summary:
      "A 5-step BullMQ pipeline that reads per-system settlement data from Cassandra, applies configurable rule formulas (scalar or 288-interval), and writes company/customer ledger entries to PostgreSQL. Triggered manually or via AEMO data sync.",
    sections: [
      {
        type: "intro",
        content:
          "The Settlement Engine is a multi-step financial calculation pipeline. A run is initiated via API (POST /api/settlements/runs/trigger), which writes a record to np_settlement_run and enqueues the first BullMQ job. Each queue step completes and chains to the next: compute snapshots → load run config → group revenue allocation → system-level allocation → company & customer ledger posting. Settlement rules are versioned formulas stored in PostgreSQL; they support both scalar (single daily value) and 288-interval (per 5-minute bucket) calculation modes.",
      },

      // ── PIPELINE ───────────────────────────────────────────────────────────
      {
        type: "stages",
        title: "BullMQ Pipeline (5 Steps in Order)",
        stages: ["se-compute-snapshots", "se-load-run", "se-group-revenue", "se-system-allocation", "se-company-customer"],
      },
      {
        type: "section",
        title: "Settlement Run Lifecycle",
        items: [
          {
            heading: "Trigger & Run ID Generation",
            body: "POST /api/settlements/runs/trigger accepts body { runDate, runScope: 'FULL'|'PARTIAL', runType: 'FINAL'|'PRELIMINARY', notes }. The controller looks up the max run_version for that date in np_settlement_run, increments it, and generates run_id as RUN-{yyyymmdd}-v{version}-{timestamp}. The row is inserted with status PENDING and triggered_by = 'MANUAL:{userId}'.",
          },
          {
            heading: "Step 0 — se-compute-snapshots",
            body: "First job: jobType compute-snapshots, jobId se-step0-{runId}. Worker calls settlementDataService.computeAllGroupSnapshots(date). For each view group it reads Cassandra table shinehub_online_settlement_data (per-system 288-array credit/debit rows), sums them into group totals, and upserts np_view_group_daily_snapshot. Same logic for control groups into np_control_group_daily_snapshot. Also computes np_system_daily_snapshot per system.",
          },
          {
            heading: "Step 1 — se-load-run",
            body: "Loads run configuration from np_settlement_run, resolves which rule bindings apply for the run date and scope, and builds a rule execution plan. Chains to Step 2.",
          },
          {
            heading: "Step 2 — se-group-revenue",
            body: "Applies GROUP_REVENUE and VIEWGROUP_REVENUE rule formulas. Reads variables from np_settlement_variable (with 5-minute cache TTL) and resolves each variable's source table — either PostgreSQL snapshots, Cassandra aggregation tables, or AEMO market tables. Formula result is written to np_control_group_settlement_day.",
          },
          {
            heading: "Step 3 — se-system-allocation",
            body: "Applies SYSTEM_ALLOCATION rule formulas. Splits group-level revenue down to individual systems proportionally (by energy contribution, capacity, or custom formula). Writes to np_system_settlement_day.",
          },
          {
            heading: "Step 4 — se-company-customer",
            body: "Applies COMPANY_REVENUE, CUSTOMER_PRICING, PARTNER_REVENUE, and COMPANY_SETTLEMENT rules. Produces ledger journal entries for each company (np_company_daily_ledger) and each customer (np_customer_daily_ledger) using ON CONFLICT (id, date) DO UPDATE. Updates np_settlement_run.status to COMPLETED and records systems_processed, groups_processed, customers_processed, total_credit, total_debit, finished_at.",
          },
        ],
      },

      // ── RULE TYPES ─────────────────────────────────────────────────────────
      {
        type: "table",
        title: "Settlement Rule Types",
        headers: ["Rule Type", "Applied In Step", "Purpose"],
        rows: [
          ["GROUP_REVENUE",      "se-group-revenue",    "Allocate NEM market revenue to each control group"],
          ["VIEWGROUP_REVENUE",  "se-group-revenue",    "Cross-company view group revenue routing"],
          ["SYSTEM_ALLOCATION",  "se-system-allocation","Split group revenue to individual battery/solar systems"],
          ["COMPANY_REVENUE",    "se-company-customer", "Route net revenue to the operating company ledger"],
          ["CUSTOMER_PRICING",   "se-company-customer", "Calculate customer-facing tariff/export credits"],
          ["PARTNER_REVENUE",    "se-company-customer", "Calculate partner (aggregator/retailer) payment"],
          ["COMPANY_SETTLEMENT", "se-company-customer", "Final inter-company netting and settlement posting"],
        ],
      },

      // ── CALCULATION MODES ──────────────────────────────────────────────────
      {
        type: "section",
        title: "Calculation Modes: Scalar vs 288-Interval",
        items: [
          {
            heading: "Scalar Mode",
            body: "The formula is evaluated once using daily aggregate values from the snapshot tables. Suitable for fixed daily fees, flat-rate revenue sharing, or monthly capacity charges amortised per day. Result is a single decimal value stored in the ledger.",
          },
          {
            heading: "288-Interval (Array) Mode",
            body: "The formula is evaluated against the _288 array fields (JSON arrays of 288 numbers) from shinehub_online_settlement_data or the group snapshots. Each of the 288 5-minute buckets produces its own result; the daily total is the sum. Used for spot-price-weighted revenue (price × energy at each interval) and FCAS payment allocation. Variables with is_array = YES in np_settlement_variable are resolved as arrays.",
          },
          {
            heading: "Formula Structure",
            body: "Rules store their formula as JSON in np_settlement_rule.formula. Example: { type: 'expression', expression: 'var1 * var2 + rate', variables: { var1: { source: 'group_snapshot', field: 'total_headroom_kw' }, var2: { source: 'market', field: 'fcas_price' } }, constants: { rate: { type: 'number', value: 0.8 } } }. The ruleCalculatorService.js resolves each variable to its DB source, fetches values (with 5-min TTL cache on np_settlement_variable lookups), and evaluates the expression.",
          },
        ],
      },

      // ── DB OPS ─────────────────────────────────────────────────────────────
      {
        type: "table",
        title: "Database Operations",
        headers: ["Operation", "Database", "Table", "Step"],
        rows: [
          ["Read per-system 288-interval settlement", "Cassandra", "shinehub_online_settlement_data", "se-compute-snapshots"],
          ["Read pre-aggregated group settlement", "Cassandra", "group_daily_settlement_data", "se-group-revenue"],
          ["Read AEMO FCAS payment data", "Cassandra", "powow_np_aemo_set_fcas_payment_aggregation_day", "se-group-revenue"],
          ["Read AEMO dispatch load data", "Cassandra", "powow_np_aemo_dispatchload_aggregation_day", "se-group-revenue"],
          ["Upsert view-group snapshot", "PostgreSQL", "np_view_group_daily_snapshot", "se-compute-snapshots"],
          ["Upsert control-group snapshot", "PostgreSQL", "np_control_group_daily_snapshot", "se-compute-snapshots"],
          ["Upsert system snapshot", "PostgreSQL", "np_system_daily_snapshot", "se-compute-snapshots"],
          ["Write group revenue result", "PostgreSQL", "np_control_group_settlement_day", "se-group-revenue"],
          ["Write system allocation result", "PostgreSQL", "np_system_settlement_day", "se-system-allocation"],
          ["Upsert company ledger", "PostgreSQL", "np_company_daily_ledger", "se-company-customer"],
          ["Upsert customer ledger", "PostgreSQL", "np_customer_daily_ledger", "se-company-customer"],
          ["Update run status/stats", "PostgreSQL", "np_settlement_run", "Every step transition"],
          ["Read rule definitions & variables", "PostgreSQL", "np_settlement_rule, np_settlement_variable", "se-group-revenue / se-system-allocation"],
        ],
      },

      // ── VARIABLE RESOLUTION ────────────────────────────────────────────────
      {
        type: "section",
        title: "Variable Resolution & Caching",
        items: [
          {
            heading: "np_settlement_variable Table",
            body: "Each variable referenced in a rule formula has a row: variable_name (PK), data_source (MYSQL | CASSANDRA | COMPUTED | PLATFORM_STATISTICS), table_name, column_name, data_type (NUMBER | STRING | DATE | BOOLEAN), is_array (YES | NO for 288-arrays), is_active. The ruleCalculatorService reads this table to know where to fetch each variable at runtime.",
          },
          {
            heading: "5-Minute TTL Cache",
            body: "To avoid hammering the DB on large runs, ruleCalculatorService.js caches np_settlement_variable lookups for 5 minutes. On a cache miss it queries PostgreSQL; on a hit it uses the cached metadata. Actual data values (e.g., today's FCAS price array) are fetched fresh per run — only the variable metadata is cached.",
          },
          {
            heading: "Error Handling",
            body: "Missing required variables throw a custom SettlementDataError — the engine does not silently skip or default to zero. Failed jobs are captured in np_settlement_run.error_message and can be retried manually via the Runs page. Partial ledger writes from failed runs are discarded on retry.",
          },
        ],
      },

      // ── AEMO INTEGRATION ───────────────────────────────────────────────────
      {
        type: "section",
        title: "AEMO Integration",
        items: [
          {
            heading: "Data Source",
            body: "AEMO settlement data is fetched from a Microsoft SQL Server instance (encrypted credentials in np_aemo_settlement_config). The AEMO sync worker queries DISPATCHLOAD and FCAS_PAYMENT tables for the relevant trading day and participant DUIDs (configured in np_aemo_duid_config).",
          },
          {
            heading: "Sync Scheduler",
            body: "The AEMO sync scheduler state is persisted in Redis key ae:scheduler:running. Schedule is CRON-driven (schedule_enabled, schedule_hour_aest, schedule_minute stored in config). Sync history and status are tracked in np_aemo_sync_run. Queue: ae-aggregation.",
          },
          {
            heading: "Preliminary vs Final Data",
            body: "AEMO publishes preliminary settlement data ~2 business days after trading day and final data ~18 business days after. Run type PRELIMINARY uses preliminary AEMO data; FINAL uses the authoritative settled values. The engine can be re-triggered with runType: 'FINAL' once final data is available — it generates a new run_version for the same date.",
          },
        ],
      },

      // ── RULE BINDING SYSTEM ────────────────────────────────────────────────
      {
        type: "section",
        title: "Rule Binding System — Overview",
        items: [
          {
            heading: "What Is a Binding?",
            body: "A binding connects a settlement rule (formula) to a specific entity (control group, view group, system allocation, customer, or company). Without a binding, a rule is defined but never applied. A single rule can be bound to multiple entities; a single entity can have multiple rules bound with different priorities and effective date ranges.",
          },
          {
            heading: "5 Binding Types",
            body: "control-group → np_control_group_revenue_rule_binding (binds GROUP_REVENUE rule to a control group). system → np_system_revenue_allocation_rule_binding (binds SYSTEM_ALLOCATION rule to a control group's system split). view-group → np_view_group_revenue_rule_binding (binds VIEWGROUP_REVENUE rule to a view group). customer → np_customer_pricing_rule_binding (binds CUSTOMER_PRICING rule to a customer, optionally scoped to a single system_id). company → special case stored directly on np_view_group_company_share.rule_id (PARTNER_REVENUE / COMPANY_REVENUE rule for the company's share of a view group).",
          },
          {
            heading: "Priority & Effective Dates",
            body: "Each binding table has priority (INTEGER, default 100) and effective_from / effective_to (DATE). The settlement engine selects bindings with ORDER BY priority ASC, effective_from DESC. Lower priority number = higher precedence. effective_to = NULL means the binding is open-ended (never expires). A binding is active for settlement_date only if effective_from ≤ settlement_date ≤ effective_to (or effective_to IS NULL).",
          },
          {
            heading: "is_active vs Effective Dates",
            body: "The binding tables have NO is_active column — date range is the sole activation mechanism. The parent rule in np_settlement_rule has is_active (SMALLINT 0/1). If the rule is inactive, all its bindings are skipped regardless of date range.",
          },
          {
            heading: "No Binding = No Revenue",
            body: "If a control group has no active GROUP_REVENUE binding for the settlement date, the engine produces zero revenue for that group in the run — there is no fallback formula. Similarly, a view group with no VIEWGROUP_REVENUE binding will have zero credits posted to its company. Always verify bindings are in place before triggering a settlement run for a new group.",
          },
          {
            heading: "params_override_json",
            body: "Each binding row has a params_override_json (JSONB) column. Values here override the rule's default constants for this specific entity. For example, the same GROUP_REVENUE rule can be bound to two control groups with different rate multipliers — each binding stores its own { rate: 0.85 } or { rate: 0.90 } in params_override_json without needing two separate rules.",
          },
        ],
      },

      // ── BINDING TABLE SCHEMAS ──────────────────────────────────────────────
      {
        type: "table",
        title: "Binding Table Schemas (all 4 entity-scoped tables share this structure)",
        headers: ["Column", "Type", "Description"],
        rows: [
          ["binding_id",          "BIGSERIAL PK",   "Auto-incremented surrogate key"],
          ["[entity]_id",         "VARCHAR(64)",     "control_group_id / view_group_id / customer_id — the entity being bound"],
          ["rule_id",             "VARCHAR(64)",     "FK → np_settlement_rule.rule_id"],
          ["params_override_json","JSONB NULL",      "Override specific formula constants for this binding only"],
          ["effective_from",      "DATE NOT NULL",   "Inclusive start date — binding applies from this trading day"],
          ["effective_to",        "DATE NULL",       "Inclusive end date — NULL = open-ended (never expires)"],
          ["priority",            "INTEGER",         "Tie-break when multiple rules match same date; lower = higher precedence (default 100)"],
          ["created_at",          "TIMESTAMP",       "Row creation timestamp"],
          ["updated_at",          "TIMESTAMP",       "Last modification timestamp"],
        ],
      },

      // ── MANDATORY SETUP PER VIEW GROUP ─────────────────────────────────────
      {
        type: "section",
        title: "Mandatory Setup Checklist: Onboarding a New View Group",
        items: [
          {
            heading: "Step 1 — Ensure a matching Control Group exists",
            body: "Every view group must have an associated control group. The control group is the operational unit used in dispatch and GROUP_REVENUE calculation. If missing, the se-group-revenue step will produce zero revenue for the entire view group.",
          },
          {
            heading: "Step 2 — Bind a GROUP_REVENUE rule to the Control Group",
            body: "POST /api/settlements/bindings/control-group with { control_group_id, rule_id, effective_from, priority }. The rule_type of the referenced rule must be GROUP_REVENUE. This defines the formula used to calculate total daily revenue for the control group from NEM market data (spot price, FCAS payments). Without this binding, the settlement run posts $0 for the group.",
          },
          {
            heading: "Step 3 — Bind a SYSTEM_ALLOCATION rule to the Control Group",
            body: "POST /api/settlements/bindings/system with { control_group_id, rule_id, effective_from }. This rule type = SYSTEM_ALLOCATION. It defines how group-level revenue is split down to individual systems (e.g., proportional to each system's headroom contribution or energy export). Without this, the se-system-allocation step cannot allocate per-system credits.",
          },
          {
            heading: "Step 4 — Bind a VIEWGROUP_REVENUE rule to the View Group",
            body: "POST /api/settlements/bindings/view-group with { view_group_id, rule_id, effective_from }. Rule type = VIEWGROUP_REVENUE. This formula routes the control group revenue through to the view group's company share. It defines the partner's visible revenue — what appears in their Account → Statement.",
          },
          {
            heading: "Step 5 — Set Company Share (Partner Revenue)",
            body: "The np_view_group_company_share table stores the percentage of view group revenue allocated to the operating company vs the partner. Setting the rule_id column on a vg_share row binds the PARTNER_REVENUE or COMPANY_REVENUE rule. This controls the final split between what the company retains and what is paid out to the partner.",
          },
          {
            heading: "Step 6 — Bind CUSTOMER_PRICING rule (if retail customers exist)",
            body: "POST /api/settlements/bindings/customer with { customer_id, rule_id, effective_from, system_id (optional) }. Rule type = CUSTOMER_PRICING. If system_id is provided, the rule applies only to that specific battery system for the customer. This step is optional for pure B2B partner arrangements but required if the group has retail customers receiving export credits.",
          },
          {
            heading: "Verifying Setup",
            body: "GET /api/settlements/bindings-summary returns counts: { rules, controlGroupBindings, systemBindings, viewGroupBindings, companyBindings, customerBindings }. All counts for your new group should be ≥ 1. Run GET /api/settlements/runs with a test date and check that all groups_processed > 0 and total_credit > 0 after completion.",
          },
        ],
      },

      // ── FORMULA EVALUATION ─────────────────────────────────────────────────
      {
        type: "section",
        title: "Formula Evaluation Engine (safeEval)",
        items: [
          {
            heading: "How Formulas Are Evaluated",
            body: "The ruleCalculatorService uses a custom safeEval() function — NOT eval() or math.js. Each variable name in the expression string is regex-replaced with its numeric value, then the resulting expression is validated against /^[\\d\\s+\\-*/().]+$/ (only digits, operators, parentheses, decimals, whitespace). If validation passes, it is executed via new Function('return (' + expr + ')')(). Non-finite or non-numeric results throw immediately.",
          },
          {
            heading: "Allowed Operators",
            body: "Only standard arithmetic: + (add), - (subtract), * (multiply), / (divide), ( ) (grouping). No function calls (Math.min, max, etc.), no ternary operators, no string operations. Formulas must be pure numeric arithmetic expressions. Example valid formulas: 'headroom_kw * price * 0.85', '(pv_energy + fcas_payment) / system_count', 'total_esell * energy_price_288 * 0.90'.",
          },
          {
            heading: "Scalar vs Array Formula Detection",
            body: "The engine inspects the formula's variables object. If any variable field name ends in _288 (e.g., pv_288, energy_price_288), the formula is evaluated 288 times — once per NEM interval. Scalar variables are broadcast (same value used for all 288 iterations). If no _288 variables exist, the formula is evaluated once against daily aggregate values.",
          },
          {
            heading: "params_override_json Integration",
            body: "Before evaluation, the binding's params_override_json is merged into the formula's constants object. Override keys take precedence over the rule defaults. This allows the same formula to run with entity-specific rates: e.g., the same GROUP_REVENUE rule bound to two control groups, one with { partner_rate: 0.85 } and another with { partner_rate: 0.90 }.",
          },
          {
            heading: "Variable Cache TTL",
            body: "np_settlement_variable metadata (table_name, column_name, is_array) is cached for 5 minutes in ruleCalculatorService to avoid repeated DB reads on large runs. Actual runtime values (e.g., today's FCAS price array) are fetched fresh per run. If you add or edit a variable definition, allow up to 5 minutes for the cache to expire before triggering a new run.",
          },
        ],
      },

      // ── AVAILABLE VARIABLES ─────────────────────────────────────────────────
      {
        type: "table",
        title: "Available Formula Variables by Rule Type",
        headers: ["Variable Name", "Type", "Source Table", "Rule Types"],
        rows: [
          // Group snapshot scalars
          ["total_headroom_kw",     "DECIMAL",    "np_control_group_daily_snapshot",  "GROUP_REVENUE"],
          ["total_footroom_kw",     "DECIMAL",    "np_control_group_daily_snapshot",  "GROUP_REVENUE"],
          ["total_epv",             "DECIMAL",    "np_control_group_daily_snapshot",  "GROUP_REVENUE"],
          ["total_esell",           "DECIMAL",    "np_control_group_daily_snapshot",  "GROUP_REVENUE"],
          ["total_ebuy",            "DECIMAL",    "np_control_group_daily_snapshot",  "GROUP_REVENUE"],
          ["system_count",          "INTEGER",    "np_control_group_daily_snapshot",  "GROUP_REVENUE"],
          ["online_pct",            "DECIMAL",    "computed",                          "GROUP_REVENUE"],
          // Group snapshot 288-arrays
          ["headroom_288",          "JSON[288]",  "np_control_group_daily_snapshot",  "GROUP_REVENUE"],
          ["footroom_288",          "JSON[288]",  "np_control_group_daily_snapshot",  "GROUP_REVENUE"],
          ["pv_288",                "JSON[288]",  "np_control_group_daily_snapshot",  "GROUP_REVENUE"],
          // Market data scalars
          ["energy_price",          "DECIMAL",    "np_market_data",                   "GROUP_REVENUE, SYSTEM_ALLOCATION"],
          ["raise_6sec_price",      "DECIMAL",    "np_market_data",                   "GROUP_REVENUE"],
          ["raise_reg_price",       "DECIMAL",    "np_market_data",                   "GROUP_REVENUE"],
          // Market data 288-arrays
          ["energy_price_288",      "JSON[288]",  "np_market_data",                   "GROUP_REVENUE, SYSTEM_ALLOCATION"],
          ["raise_6sec_price_288",  "JSON[288]",  "np_market_data",                   "GROUP_REVENUE"],
          // AEMO settlement
          ["fcas_payment_today",    "DECIMAL",    "powow_np_aemo_set_fcas_payment_aggregation_day", "GROUP_REVENUE"],
          ["fcas_payment_288",      "JSON[288]",  "powow_np_aemo_set_fcas_payment_aggregation_day", "GROUP_REVENUE"],
          // System snapshot scalars (SYSTEM_ALLOCATION)
          ["system_headroom_kw",    "DECIMAL",    "np_system_daily_snapshot",         "SYSTEM_ALLOCATION"],
          ["system_epv",            "DECIMAL",    "np_system_daily_snapshot",         "SYSTEM_ALLOCATION"],
          ["system_esell",          "DECIMAL",    "np_system_daily_snapshot",         "SYSTEM_ALLOCATION"],
          ["system_revenue",        "DECIMAL",    "np_control_group_settlement_day",  "SYSTEM_ALLOCATION"],
          ["inverter_kw",           "DECIMAL",    "np_system",                        "SYSTEM_ALLOCATION"],
          ["battery_kwh",           "DECIMAL",    "np_system",                        "SYSTEM_ALLOCATION"],
        ],
      },

      // ── RUN STATUS TABLE ───────────────────────────────────────────────────
      {
        type: "table",
        title: "Settlement Run Statuses",
        headers: ["Status", "Meaning", "Next Action"],
        rows: [
          ["PENDING",   "Run record created; first BullMQ job not yet picked up",    "Automatic — worker picks up within seconds"],
          ["RUNNING",   "One of the 5 pipeline steps is actively executing",          "Monitor via GET /api/settlements/runs/:runId"],
          ["COMPLETED", "All 5 steps finished; ledgers updated; statements ready",    "Review in Settlements / Statement / Receivable"],
          ["FAILED",    "A pipeline step threw an unrecoverable error; ledgers rolled back", "Check error_message; fix root cause; retrigger"],
        ],
      },

      // ── API ENDPOINTS ──────────────────────────────────────────────────────
      {
        type: "table",
        title: "API Endpoints",
        headers: ["Method", "Path", "Description"],
        rows: [
          ["GET",  "/api/settlements/runs",                          "List runs with filter (status, runType, date range)"],
          ["GET",  "/api/settlements/runs/:runId",                   "Run details including error_message and processing stats"],
          ["GET",  "/api/settlements/runs/:runId/groups",            "Per-group breakdown for a completed run"],
          ["POST", "/api/settlements/runs/trigger",                  "Start a new settlement run (FULL or PARTIAL, FINAL or PRELIMINARY)"],
          ["PUT",  "/api/settlements/runs/:runId/cancel",            "Cancel a PENDING or RUNNING settlement"],
          ["POST", "/api/settlements/snapshots/compute",             "Manually re-compute group snapshots for a date without triggering a full run"],
          ["GET",  "/api/settlements/rules",                         "List settlement rules (versioned formulas)"],
          ["POST", "/api/settlements/rules",                         "Create a new rule"],
          ["PUT",  "/api/settlements/rules/:ruleId",                 "Update rule formula / variables (creates new version)"],
          ["GET",  "/api/settlements/bindings/:bindingType",         "Read rule-to-group bindings"],
          ["POST", "/api/settlements/bindings/:bindingType",         "Bind a rule to a group/company"],
          ["GET",  "/api/settlements/journals",                      "List special journal entries (manual credits/debits)"],
          ["POST", "/api/settlements/journals",                      "Create manual journal entry (requires Superadmin approval)"],
          ["PUT",  "/api/settlements/journals/mark-applied",         "Mark pending journals as applied to next statement cycle"],
          ["GET",  "/api/settlements/ledger/company",                "Company daily ledger entries"],
          ["GET",  "/api/settlements/ledger/customer",               "Customer daily ledger entries"],
        ],
      },

      {
        type: "callout",
        variant: "warning",
        content:
          "Never directly modify np_company_daily_ledger, np_customer_daily_ledger, or any settlement table outside the Special Journals workflow. Direct writes corrupt the rule_snapshot_hash checksum stored in np_settlement_run and will invalidate AEMO compliance audit trails. Always use Special Journals → Superadmin approval flow for manual corrections.",
      },
      {
        type: "screenshot",
        src: imgSettlements,
        caption: "Settlements page — view daily settlement runs, statuses, and per-group financial breakdown",
      },
    ],
  },
];

// ---------------------------------------------------------------------------
// BLOCK RENDERERS
// ---------------------------------------------------------------------------

function renderEngineBlock(block, idx, accentColor) {
  switch (block.type) {
    case "intro":
      return (
        <Paragraph key={idx} className="eg-block-intro">
          {block.content}
        </Paragraph>
      );

    case "stages":
      return (
        <div key={idx} className="eg-stages-wrap">
          {block.title && <div className="eg-sub-header" style={{ borderLeftColor: accentColor }}>{block.title}</div>}
          <div className="eg-stages-row">
            {block.stages.map((stage, i) => (
              <React.Fragment key={i}>
                <div className="eg-stage-block" style={{ background: accentColor === "#e8a020" ? "#1c3557" : "#1c3557" }}>
                  {stage}
                </div>
                {i < block.stages.length - 1 && (
                  <RightOutlined className="eg-stage-arrow" style={{ color: accentColor }} />
                )}
              </React.Fragment>
            ))}
          </div>
        </div>
      );

    case "section":
      return (
        <div key={idx} className="eg-section-wrap">
          {block.title && <div className="eg-sub-header" style={{ borderLeftColor: accentColor }}>{block.title}</div>}
          <div className="eg-items-list">
            {block.items.map((item, i) => (
              <div key={i} className="eg-item-row">
                <CheckCircleOutlined className="eg-item-icon" style={{ color: accentColor }} />
                <div className="eg-item-body">
                  <div className="eg-item-heading">{item.heading}</div>
                  <div className="eg-item-text">{item.body}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      );

    case "table":
      return (
        <div key={idx} className="eg-table-wrap">
          {block.title && <div className="eg-sub-header" style={{ borderLeftColor: accentColor }}>{block.title}</div>}
          <table className="eg-table">
            {block.headers && (
              <thead>
                <tr>
                  {block.headers.map((h, i) => <th key={i}>{h}</th>)}
                </tr>
              </thead>
            )}
            <tbody>
              {(block.rows || []).map((row, i) => (
                <tr key={i}>
                  {row.map((cell, j) => <td key={j}>{cell}</td>)}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      );

    case "callout":
      return (
        <div
          key={idx}
          className={`eg-callout eg-callout--${block.variant || "info"}`}
        >
          <InfoCircleOutlined className="eg-callout-icon" />
          <span>{block.content}</span>
        </div>
      );

    case "screenshot":
      return (
        <div key={idx} className="eg-screenshot-wrap">
          <img
            src={block.src}
            alt={block.caption || "Screenshot"}
            className="eg-screenshot-img"
          />
          {block.caption && (
            <div className="eg-screenshot-caption">{block.caption}</div>
          )}
        </div>
      );

    default:
      return null;
  }
}

// ---------------------------------------------------------------------------
// ENGINE SUMMARY CARD (initial 2-card view)
// ---------------------------------------------------------------------------

function EngineSummaryCard({ engine, onClick }) {
  return (
    <Card
      className="eg-summary-card"
      hoverable
      onClick={() => onClick(engine)}
    >
      <div className="eg-summary-accent" style={{ background: engine.color }} />
      <div className="eg-summary-body">
        <div
          className="eg-summary-icon"
          style={{ color: engine.color, background: engine.color + "18" }}
        >
          {engine.icon}
        </div>
        <div className="eg-summary-content">
          <div className="eg-summary-top">
            <div className="eg-summary-title">{engine.title}</div>
            <Tag color={engine.tagColor} className="eg-summary-tag">
              {engine.tagLabel}
            </Tag>
          </div>
          <div className="eg-summary-desc">{engine.summary}</div>
          <div className="eg-summary-cta" style={{ color: engine.color }}>
            View documentation <ArrowRightOutlined className="eg-summary-cta-arrow" />
          </div>
        </div>
      </div>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// ENGINE DETAIL VIEW (inline documentation panel)
// ---------------------------------------------------------------------------

function EngineDetailPanel({ engine, onBack }) {
  return (
    <div className="eg-detail-panel">
      {/* Header */}
      <div className="eg-detail-header" style={{ borderTopColor: engine.color }}>
        <div className="eg-detail-header-left">
          <Button
            icon={<ArrowLeftOutlined />}
            onClick={onBack}
            className="eg-back-btn"
            type="text"
          >
            Back to Engine Guide
          </Button>
          <div className="eg-detail-title-row">
            <span
              className="eg-detail-icon"
              style={{ color: engine.color, background: engine.color + "18" }}
            >
              {engine.icon}
            </span>
            <div>
              <div className="eg-detail-title">{engine.title}</div>
              <div className="eg-detail-subtitle">{engine.summary}</div>
            </div>
          </div>
        </div>
        <Tag color={engine.tagColor} className="eg-detail-tag">
          {engine.tagLabel}
        </Tag>
      </div>

      {/* Documentation sections */}
      <div className="eg-detail-body">
        {engine.sections.map((block, idx) =>
          renderEngineBlock(block, idx, engine.accentColor)
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// MAIN COMPONENT
// ---------------------------------------------------------------------------

export default function EngineGuide() {
  const navigate = useNavigate();
  const [selectedEngine, setSelectedEngine] = useState(null);

  const roleType = localStorage.getItem("roleType") || "";
  if (roleType !== "SUPERADMIN") {
    return (
      <PageLayout active="engineGuide">
        <div className="eg-page">
          <Result
            status="403"
            title="Access Restricted"
            subTitle="Engine Guide is available to Superadmin users only."
            extra={
              <Button type="primary" onClick={() => navigate("/profile")}>
                Go to Profile
              </Button>
            }
          />
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout active="engineGuide">
      <div className="eg-page">
        {/* ── PAGE HEADER ── */}
        <div className="eg-page-header">
          <div className="eg-page-brand">
            <CloudServerOutlined className="eg-page-brand-icon" />
            <Title level={4} className="eg-page-brand-title">Engine Guide</Title>
          </div>
          <Text className="eg-page-subtitle">
            Technical documentation for the Realtime and Settlement engines powering the VPP platform.
          </Text>
        </div>

        {/* ── CONTENT ── */}
        {selectedEngine ? (
          <EngineDetailPanel
            engine={selectedEngine}
            onBack={() => setSelectedEngine(null)}
          />
        ) : (
          <div className="eg-cards-grid">
            {ENGINES.map((engine) => (
              <EngineSummaryCard
                key={engine.id}
                engine={engine}
                onClick={setSelectedEngine}
              />
            ))}
          </div>
        )}
      </div>
    </PageLayout>
  );
}
